import React, { useEffect, useMemo, useState } from "react";
import _ from "lodash";
import { useSelector } from "react-redux";
import { Course } from "src/types/coaching/courses";
import { useDispatch } from "react-redux";

import TextareaAutosize from "react-autosize-textarea";
import { fetchLessonForCourse, fetchLessonPreviewForCourse, updateLessonProperty } from "src/redux/actions/coaching/courses";

import Reveal from "src/components/animation";
import { AssetViewer } from "src/pages/asset/components/assetViewerEx";

import styles from "./lesson.module.scss";
import useMeasure from "react-use-measure";
import EmptyMessage from "src/components/emptyMessage";

interface EditLessonContentProps {
  course: Course;
  lesson: any;
}

export const EditLessonContent: React.FC<EditLessonContentProps> = ({ course, lesson }) => {
  const dispatch = useDispatch();

  const [assetViewerRef, assetViewerBounds] = useMeasure();

  const assetId = useMemo(() => {
    return _.get(lesson, "content.id");
  }, [lesson]);

  const loading = useSelector((state: any) => {
    return _.get(state, `courses.edit.assets['${assetId}'].loading`);
  });

  const asset = useSelector((state: any) => {
    const item = _.get(state, `courses.edit.assets['${assetId}'].item`);
    if (item?.id !== assetId) {
      return null;
    }

    return item;
  });

  const preview = useSelector((state: any) => {
    const loading = _.get(state, `courses.edit.assets['${assetId}'].loading`);
    const previewItem = _.get(state, `courses.edit.assets['${assetId}'].preview`);
    return {
      loading: loading || previewItem?.loading,
      item: previewItem?.item,
    };
  });

  const nameError = useSelector((state: any) => {
    const errors = _.get(state.courses, `edit.errors`) || [];
    return _.find(errors, { type: 'lesson', uuid: lesson.uuid, property: 'name' });
  });

  const descriptionError = useSelector((state: any) => {
    const errors = _.get(state.courses, `edit.errors`) || [];
    return _.find(errors, { type: 'lesson', uuid: lesson.uuid, property: 'description' });
  });

  const [name,setName] = useState(lesson?.name);
  const [description, setDescription] = useState(lesson?.description);

  useEffect(() => {
    if (assetId) {
      dispatch(fetchLessonForCourse({
        course: course.id!,
        lesson: lesson.id,
        asset: assetId
      }));
    }
  }, [assetId]);

  const handlePropertyChange = (property: string, value: any) => {
    dispatch(updateLessonProperty({
      course: course.id!,
      lesson: lesson.uuid,
      property,
      value
    }));
  }

  const handleNameChange = (e: any) => {
    const content = e.target.value;
    setName(content);
    handlePropertyChange("name", content);
  }

  const handleDescriptionChange = (e: any) => {
    const content = e.target.value;
    setDescription(content);
    handlePropertyChange("description", content);
  }

    useEffect(() => {
      setName(lesson?.name || "");
    }, [lesson]);

    useEffect(() => {
      setDescription(lesson?.description || "")
    },[lesson])

  const handleFetchPreviewData = () => {
    if (asset) {
      dispatch(fetchLessonPreviewForCourse({
        course: course.id!,
        lesson: lesson.id,
        asset: asset.id,
        includeText: true,
      }));
    }
  }

  const recordSessionAnalytics = () => {

  };

  const handleAnalyticsChange = (data) => {

  }

  return (
    <div className={styles.editContainer}>
      <div className={styles.header}>
        <div className={styles.field}>
          <input className={styles.name}
            value={name}
            placeholder="Enter Lesson name..."
            onChange={handleNameChange}
          />
          <Reveal show={nameError != null}>
            <div className={styles.error}>
              <i className="fa-solid fa-triangle-exclamation"></i>
              <span>{nameError?.message}</span>
            </div>
          </Reveal>
        </div>
        <div className={styles.field}>
          <TextareaAutosize
            className={styles.description}
            rows={1}
            maxRows={6}
            value={description}
            placeholder={'Type here the description...'}
            spellCheck={true}
            onChange={handleDescriptionChange}
            onPointerEnterCapture={() => { }}
            onPointerLeaveCapture={() => { }}
          />
          <Reveal show={descriptionError != null}>
            <div className={styles.error}>
              <i className="fa-solid fa-triangle-exclamation"></i>
              <span>{descriptionError?.message}</span>
            </div>
          </Reveal>
        </div>
      </div>

      <div ref={assetViewerRef} className={styles.assetContainer}>
        {loading && (
          <div className={styles.loading}>
            <img src={'/images/loading_plane.gif'} />
            <div className={styles.message}>Loading lesson...</div>
          </div>
        )}
        {!loading && asset &&
          <AssetViewer
            key={asset.id}
            className={styles.assetViewer}
            asset={asset}
            offset={assetViewerBounds.top + 20}
            preferredWidth={assetViewerBounds.width || 1024}
            scrollParent={`.${styles.editContainer}`}

            preview={preview}
            fetchPreviewData={handleFetchPreviewData}

            recordSessionAnalytics={recordSessionAnalytics}
            onAnalyticsChange={handleAnalyticsChange}
          />
        }

        {!loading && !asset && (
          <div className={styles.notFoundContainer}>
            <EmptyMessage
              width={500}
              image="/images/assets/404error_message.svg"
              heading="Mmm... who moved my cheese?"
              message="Sorry!  Some one passed you the wrong information. You were never supposed to see me :). This asset does not exist any longer."
            />
          </div>
        )}
      </div>
    </div>
  );
}
